import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { CACHE_EXPIRATION_TIME } from 'app/common/personalConstants';
import { PaymentAccount, PaymentState } from 'app/shared/models/claim-tracker.model';
import { ClaimsTrackerService } from 'app/shared/services/claims-tracker.service';
import { LoggerService } from 'app/shared/services/logger.service';
import { SessionService } from 'app/shared/services/session.service';
import { UtilService } from 'app/shared/services/util.service';
import { environment } from 'environments/environment';
import { map, shareReplay } from 'rxjs';
import { PaymentPreferencesComponent } from '../payment-preferences/payment-preferences.component';

@Component({
    selector: 'is-payments',
    templateUrl: './payments.component.html',
    styleUrls: ['./payments.component.scss'],
    standalone: false
})
export class PaymentsComponent implements OnInit {
    @ViewChild(PaymentPreferencesComponent) paymentPreferencesModal!: PaymentPreferencesComponent;
    @Input() claimNumber!: string;
    @Input() contactPublicId!: any;
    @Input() isPaymentsNotApplicable!: boolean;
    @Input() displayUnregistered: boolean = false;
    environment = environment;

    get isUnregisteredUser(): boolean {
        return this.displayUnregistered;
    }

    paymentState: PaymentState = PaymentState.LOADING;
    paymentAccount: PaymentAccount = PaymentAccount.NO_BANK_ACCOUNT;
    readonly PaymentStateEnum = PaymentState;
    readonly PaymentAccountEnum = PaymentAccount;
    paymentsSummary: { displayInfo: any[] } = {
        displayInfo: []
    };
    private hasLoaded = false;
    private exposureDict: { exposureId: string; exposureType: string }[] = [];
    bankAccountDetails: any;
    isError!: boolean;
    currentPage = 1;
    totalPages = 1;
    limit = 10;
    @Input() useCaching!: boolean;

    policyNumber!: string;
    userType!: string;

    constructor (
        private claimsTrackerService: ClaimsTrackerService,
        private logger: LoggerService,
        private bp: BreakpointObserver,
        private utils: UtilService,
        private sessionService: SessionService
    ) {

    }

    delay(ms: number): Promise<void> {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    ngOnInit(): void {
        if (this.displayUnregistered) {
            this.paymentState = PaymentState.NO_PAYMENTS;
            return;
        }
        if (!this.hasLoaded && this.claimNumber) {
            this.initializeSessionData();
            this.initializeLogger();
            this.hasLoaded = true;
            this.bp.observe('(max-width: 575px)')
            .pipe(
                map((result) => result.matches),
                shareReplay(1)
            )
            .subscribe((isMobile) => {
                this.limit = isMobile ? 6 : 10;
                this.loadPayments((this.currentPage - 1) * this.limit, this.limit);
            });

            const BANK_ACCOUNTS_CACHE_DATA = this.useCaching ? this.utils.getWithExpiry('BANK_ACCOUNTS_DETAILS') : undefined;
            if (
                typeof BANK_ACCOUNTS_CACHE_DATA === 'undefined' ||
                BANK_ACCOUNTS_CACHE_DATA.id !== this.claimNumber
            ) {
                this.loadBankAccounts();
            } else {
                const bankDetails = BANK_ACCOUNTS_CACHE_DATA.value;
                this.loadBankDetails(bankDetails);
            }
        }
    }

    gotoPage(event: Event): void {
        const customEvent = <CustomEvent<{ page: number }>>event;
        this.currentPage = customEvent.detail.page;
        this.loadPayments((this.currentPage - 1) * this.limit, this.limit);
    }

    private initializeLogger(): void {
        this.logger.setComponentName('PaymentsComponent');
        this.logger.setPageName('Payments');
        this.logger.info('PaymentsComponent - Component Initialized', {
            claimNumber: this.claimNumber,
            policyNumber: this.policyNumber,
            userType: this.userType
        });
    }

    private initializeSessionData(): void {
        this.claimNumber = this.sessionService.getClaimNumber();
        this.contactPublicId = this.sessionService.getSessionItem('claimsContactPublicId');
        this.policyNumber = this.sessionService.getPolicyNumber();
        this.userType = this.sessionService.getUserType();
    }

    loadPayments(offset: number, limit: number): void {
        this.paymentState = PaymentState.LOADING;
        this.paymentsSummary.displayInfo = [];
        this.isError = false;

        const EXPOSURES_CACHE_DATA = this.useCaching ? this.utils.getWithExpiry('EXPOSURES_DATA') : undefined;

        if (typeof EXPOSURES_CACHE_DATA === 'undefined' || EXPOSURES_CACHE_DATA.id !== this.claimNumber) {
            this.claimsTrackerService.getClaimsExposures(this.claimNumber).subscribe({
                next: (exposures) => {
                    this.utils.setWithExpiry(
                        'EXPOSURES_DATA',
                        exposures,
                        CACHE_EXPIRATION_TIME,
                        this.claimNumber
                    );
                    this.exposureDict = this.buildExposureDictionary(exposures);
                    this.loadPaymentsData(offset, limit);
                },
                error: (error) => {
                    this.handleError('loading exposures', error);
                }
            });
        } else {
            const exposures = EXPOSURES_CACHE_DATA.value;
            this.exposureDict = this.buildExposureDictionary(exposures);
            this.loadPaymentsData(offset, limit);
        }
    }

    private buildExposureDictionary(exposures: any): { exposureId: string; exposureType: string }[] {
        if (!exposures?.exposures) {
            return [];
        }

        return exposures.exposures.map((exposure: any) => ({
            exposureId: exposure.publicID,
            exposureType: exposure.exposureType
        }));
    }

    private loadPaymentsData(offset: number, limit: number): void {
        this.claimsTrackerService.getClaimPaymentsProperty(this.claimNumber, offset, limit).subscribe({
            next: (payments) => {
                this.calculatePagination(payments.totalCount, limit);
                this.processPayments(payments);
            },
            error: (error) => {
                this.handleError('Error loading payments', error);
            }
        });
    }

    private calculatePagination(totalCount: any, limit: number): void {
        const total = Number(totalCount ?? 0);
        const safeLimit = Number(limit) > 0 ? Number(limit) : 1;
        this.totalPages = total > 0 ? Math.ceil(total / safeLimit) : 0;
    }

    private processPayments(payments: any): void {
        if (!payments?.getPayments || payments.getPayments.length === 0) {
            this.logger.info('No payment returned');
            this.paymentState = PaymentState.NO_PAYMENTS;
            return;
        }

        const simplifiedPayments = payments.getPayments
            .map((payment: any) => this.transformPayment(payment))
            .filter((payment: any) => payment !== null);

        this.paymentsSummary.displayInfo = simplifiedPayments;
        this.paymentState = simplifiedPayments.length > 0 ?
            PaymentState.HAS_PAYMENTS :
            PaymentState.NO_PAYMENTS;
    }

    private transformPayment(payment: any): any | null {
        if (!payment.paymentItems || payment.paymentItems.length === 0) {
            return null;
        }

        const firstItem = payment.paymentItems[0];
        const costType = firstItem.costType;
        const exposureType = this.getExposureType(firstItem.exposurePublicID);

        if (!this.shouldIncludePayment(costType, exposureType)) {
            return null;
        }

        return {
            issueTo: this.formatFieldName(payment.payee || ''),
            issueDate: this.formatIssueDate(payment.issueDate),
            amount: this.formatAmount(payment.paymentAmount),
            description: firstItem.paymentDescription || '',
            referenceNumber: payment.referenceNumber || '',
            status: this.mapPaymentStatus(payment.status),
            type: payment.paymentMethod && payment.paymentMethod.toLowerCase() === 'eft' ?
            'EFT' :
            this.formatFieldName(payment.paymentMethod || '')
        };
    }

    formatFieldName(name: string): string {
        if (!name || typeof name !== 'string') {
            return '';
        }
        return name
            .toLowerCase()
            .split(' ')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    }

    private getExposureType(exposureId: string): string | undefined {
        const exposure = this.exposureDict.find((expo) => expo.exposureId === exposureId);
        return exposure?.exposureType;
    }

    private shouldIncludePayment(costType: string, exposureType: string | undefined): boolean {
        const excludedExposureTypes = ['Med Pay', 'BodilyInjuryDamage', 'PIPDamages'];
        return costType === 'indemnity' && !excludedExposureTypes.includes(exposureType || '');
    }

     private formatIssueDate(issueDate: string): string {
        if (!issueDate) return '';

        const date = new Date(issueDate);
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();

        if (typeof window !== 'undefined') {
            const isMobile = window.matchMedia('(max-width: 575px)').matches;
            if (isMobile) {
                return `${month}/${day}/${year}`;
            }
            return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
        }
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    }

    private formatAmount(paymentAmount: string): string {
        if (!paymentAmount) {
            return '';
        }
        try {
            const numeric = parseFloat(paymentAmount.replace(/[^0-9.-]+/g, ''));
            return isNaN(numeric) ? paymentAmount : `$${numeric.toFixed(2)}`;
        } catch {
            return paymentAmount;
        }
    }

    private mapPaymentStatus(status: string): string {
        if (!status) {
            return '';
        }

        const statusMap: { [key: string]: string } = {
            'pendingvoid': 'Payment Stopped',
            'voided': 'Payment Stopped',
            'pendingstop': 'Payment Stopped',
            'stopped': 'Payment Stopped',
            'requesting': 'Issued',
            'requested': 'Issued',
            'notifying': 'Issued',
            'issued': 'Issued',
            'cleared': 'Cleared'
        };

        return statusMap[status.toLowerCase()] || status;
    }

    private handleError(message: string, error: any): void {
        this.logger.error(`Property Payments: Error ${message}`, error);
        this.isError = true;
        this.paymentState = PaymentState.ERROR;
    }

    loadBankAccounts(): void {
        this.isError = false;
        this.paymentAccount = PaymentAccount.LOADING;
        this.claimsTrackerService.getBankAccounts(this.claimNumber, this.contactPublicId).subscribe(
            (bankAccountDetails) => {
                this.loadBankDetails(bankAccountDetails);
                this.utils.setWithExpiry(
                    'BANK_ACCOUNTS_DETAILS',
                    bankAccountDetails,
                    CACHE_EXPIRATION_TIME,
                    this.claimNumber
                );
            },
            (error) => {
                this.logger.error('Property Payments: Error loading bankAccountDetails', error);
                this.isError = true;
                this.paymentAccount = PaymentAccount.ERROR;
            }
        );
    }

    loadBankDetails(bankAccountDetails): void {
        if (!bankAccountDetails || (Array.isArray(bankAccountDetails) && bankAccountDetails.length === 0)) {
            this.logger.info('No bank account details returned');
            if (this.paymentState !== PaymentState.HAS_PAYMENTS) {
                this.paymentAccount = PaymentAccount.NO_BANK_ACCOUNT;
            }
        } else if (Array.isArray(bankAccountDetails)) {
            const primaryAccount = bankAccountDetails.find((account: any) => account.primaryAccount === true);
            const accountToUse = primaryAccount || bankAccountDetails[0];
            if (accountToUse) {
                this.bankAccountDetails = {
                    bankName: this.formatFieldName(accountToUse.bankName),
                    bankAccountType: this.formatFieldName(accountToUse.bankAccountType),
                    accountName: accountToUse.accountName,
                    bankRoutingNumber: accountToUse.bankRoutingNumber,
                    bankAccountNumber: accountToUse.bankAccountNumber
                };
                this.paymentAccount = PaymentAccount.HAS_BANK_ACCOUNT;
            }
        }
    }

    openPaymentPreferences(): void {
        this.logger.info(`Property Claim Tracker - Payment preferences modal opened`);
        if (this.paymentPreferencesModal) {
            this.paymentPreferencesModal.open();
        } else {
            this.logger.error('PaymentPreferencesComponent is not initialized.');
        }
    }

    onPaymentPreferencesClose(event: { status: 'success' | 'failure' | 'dismiss', message: string }): void {
        this.logger.info(`Payment preferences modal closed with status: ${event.status}`);
        if (event.status === 'success') {
            this.loadBankAccounts();
        }
    }
}

